import type { BaseProvider } from './base'
import type { EventData, EventFilter, GlobalContext } from '../types/events'
import { GoogleAnalyticsConfig } from '../types/providers'

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>
    gtag?: (...args: unknown[]) => void
  }
}

export class GoogleAnalyticsProvider implements BaseProvider {
  private config: GoogleAnalyticsConfig
  private isInitialized = false
  private globalContext: GlobalContext = {}
  public eventFilter?: EventFilter

  constructor(config: GoogleAnalyticsConfig, eventFilter?: EventFilter) {
    this.config = config
    this.eventFilter = eventFilter
  }

  public initialize(): void {
    if (this.isInitialized) return

    if (this.config.method === 'gtag') {
      this.initializeGtag()
    } else {
      this.initializeGtm()
    }

    this.isInitialized = true
    if (this.config.debug) {
      // eslint-disable-next-line no-console
      console.log(`[Analytics] Initialized with ${this.config.method}`)
    }
  }

  private initializeGtag(): void {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.measurementId}`
    document.head.appendChild(script)

    const inlineScript = document.createElement('script')
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${this.config.measurementId}');
    `
    document.head.appendChild(inlineScript)
  }

  private initializeGtm(): void {
    const script = document.createElement('script')
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${this.config.measurementId}');
    `

    if (document.head.firstChild) {
      document.head.insertBefore(script, document.head.firstChild)
    } else {
      document.head.appendChild(script)
    }

    const noscript = document.createElement('noscript')
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.config.measurementId}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'
    noscript.appendChild(iframe)

    if (document.body.firstChild) {
      document.body.insertBefore(noscript, document.body.firstChild)
    } else {
      document.body.appendChild(noscript)
    }
  }

  public setGlobalContext(context: GlobalContext): void {
    this.globalContext = {
      ...this.globalContext,
      ...context,
    }

    const userProperties = {
      user_id: context.user_id,
      domain: context.domain,
    }

    const customDimensions = {
      vehicle_year: context.vehicle_year,
      vehicle_make: context.vehicle_make,
      vehicle_model: context.vehicle_model,
      vehicle_engine: context.vehicle_engine,
      vehicle_id: context.vehicle_id,
    }

    if (this.config.method === 'gtag' && window.gtag) {
      if (userProperties.user_id || userProperties.domain) {
        window.gtag('set', 'user_properties', userProperties)
      }
      if (Object.keys(customDimensions).length > 0) {
        window.gtag('set', customDimensions)
      }
    } else if (window.dataLayer) {
      window.dataLayer.push({
        event: 'set_global_context',
        ...userProperties,
        ...customDimensions,
      })
    }
  }

  public clearGlobalContext(keys?: string[]): void {
    if (!keys) {
      this.globalContext = {}
    } else {
      keys.forEach((key) => {
        delete this.globalContext[key]
      })
    }

    // Handle specific case for vehicle data clearing
    const vehicleKeys = [
      'vehicle_year',
      'vehicle_make',
      'vehicle_model',
      'vehicle_engine',
      'vehicle_id',
    ]

    // Check if we're clearing any vehicle keys
    const clearingVehicle =
      !keys || keys.some((key) => vehicleKeys.includes(key))

    if (this.config.method === 'gtag' && window.gtag) {
      // If clearing vehicle data, explicitly set those to undefined
      if (clearingVehicle) {
        window.gtag('set', {
          vehicle_year: null,
          vehicle_make: null,
          vehicle_model: null,
          vehicle_engine: null,
          vehicle_id: null,
        })
      }

      // Set remaining context
      window.gtag('set', {
        ...this.globalContext,
      })
    } else if (window.dataLayer) {
      // If clearing vehicle data, explicitly push null values to clear GTM variables
      if (clearingVehicle) {
        window.dataLayer.push({
          vehicle_year: null,
          vehicle_make: null,
          vehicle_model: null,
          vehicle_engine: null,
          vehicle_id: null,
        })

        // Force a reset by setting a specific event
        window.dataLayer.push({
          event: 'clear_vehicle_data',
        })
      }

      // Push remaining context with clear event
      window.dataLayer.push({
        event: 'clear_global_context',
        ...this.globalContext,
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  public async trackEvent(
    eventName: string,
    eventData: EventData = {}
  ): Promise<void> {
    if (typeof window === 'undefined') {
      return
    }

    try {
      const shouldClearVehicleData = eventData.__clearVehicleData === true
      const { __clearVehicleData, ...cleanEventData } = eventData

      if (this.config.method === 'gtag' && window.gtag) {
        if (shouldClearVehicleData) {
          window.gtag('set', {
            vehicle_year: null,
            vehicle_make: null,
            vehicle_model: null,
            vehicle_engine: null,
            vehicle_id: null,
          })
        }
        window.gtag('event', eventName, cleanEventData)
      } else if (window.dataLayer) {
        if (shouldClearVehicleData) {
          window.dataLayer.push({
            vehicle_year: null,
            vehicle_make: null,
            vehicle_model: null,
            vehicle_engine: null,
            vehicle_id: null,
          })

          // Force a reset by setting a specific event just for clearing vehicle data
          window.dataLayer.push({
            event: 'clear_vehicle_data',
          })
        }
        window.dataLayer.push({ items: null })
        window.dataLayer.push({
          event: eventName,
          ...cleanEventData,
        })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to track event:', error)
    }
  }
}
